import {ScreenshotPayload} from 'components/customer/RoomPlanner/types';

/**
 * Enum representing the types of events that can be published or subscribed to.
 */
export enum Event {
    ROOM_PLANNER_SNAPSHOT = 'ROOM_PLANNER_SNAPSHOT',
    ROOM_PLANNER_SNAPSHOT_COMPLETED = 'ROOM_PLANNER_SNAPSHOT_COMPLETED',
    ROOM_PLANNER_CART_DELETE = 'ROOM_PLANNER_CART_DELETE',
    ROOM_PLANNER_CLEAR_PRODUCT_EDITOR = 'ROOM_PLANNER_CLEAR_PRODUCT_EDITOR',
    ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR = 'ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR',
}

/**
 * Defines the payload types associated with each event type.
 */
export type EventPayloads = {
    [Event.ROOM_PLANNER_SNAPSHOT]: ScreenshotPayload;
    [Event.ROOM_PLANNER_SNAPSHOT_COMPLETED]: string;
    [Event.ROOM_PLANNER_CART_DELETE]: number;
    [Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR]: void;
    [Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR]: void;
};

/**
 * A callback function type that handles the payload for a specific event.
 * Subscribers may report success/failure (e.g. whether a form save
 * completed) by resolving with a boolean; `publish` collects these so
 * callers can gate follow-up actions on the outcome.
 *
 * @template K - The event type.
 */
type Callback<K extends Event> = (
    data: EventPayloads[K]
) => void | boolean | Promise<void | boolean>;

/**
 * Configuration options for the GenericPubSub instance.
 */
interface PubSubOptions {
    /**
     * Whether to log internal pub/sub actions to the console.
     */
    log?: boolean;
}

/**
 * A generic, strongly-typed publish-subscribe system for handling events.
 */
class GenericPubSub {
    /**
     * Internal event-to-callbacks map.
     */
    private events: {
        [K in Event]: Callback<K>[];
    } = {
        [Event.ROOM_PLANNER_SNAPSHOT]: [],
        [Event.ROOM_PLANNER_SNAPSHOT_COMPLETED]: [],
        [Event.ROOM_PLANNER_CART_DELETE]: [],
        [Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR]: [],
        [Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR]: [],
    };

    /**
     * Whether to log subscription, publication, and unsubscription actions.
     */
    private log: boolean;

    /**
     * Creates an instance of GenericPubSub.
     *
     * @param {PubSubOptions} options - Optional configuration.
     */
    constructor({log = false}: PubSubOptions) {
        this.log = log;
    }

    /**
     * Subscribes a callback to a specific event.
     *
     * @template K
     * @param {K} event - The event to subscribe to.
     * @param {Callback<K>} callback - The function to invoke when the event is published.
     * @return {void}
     */
    subscribe<K extends Event>(event: K, callback: Callback<K>): void {
        // eslint-disable-next-line security/detect-object-injection -- pass along generic payload
        this.events[event].push(callback);
    }

    /**
     * Publishes an event and waits for all subscriber callbacks to finish,
     * collecting whatever each one resolves with.
     *
     * @param {*} event - The event to publish.
     * @param {*} data - The data payload for the event.
     * @return {*} A promise resolving with each subscriber's return value.
     */
    async publish<K extends Event>(
        event: K,
        data: EventPayloads[K]
    ): Promise<Array<void | boolean>> {
        // eslint-disable-next-line security/detect-object-injection -- pass along generic payload
        const subscribers = this.events[event];

        return Promise.all(subscribers.map((cb) => cb(data)));
    }

    /**
     * Unsubscribes a callback from a specific event.
     *
     * @template K
     * @param {K} event - The event to unsubscribe from.
     * @param {Callback<K>} callback - The function to remove.
     * @return {void}
     */
    unsubscribe<K extends Event>(event: K, callback: Callback<K>): void {
        // eslint-disable-next-line security/detect-object-injection -- pass along generic payload
        this.events[event] = this.events[event].filter((cb) => cb !== callback);
    }
}

/**
 * Singleton instance of the GenericPubSub for use throughout the app.
 */
export const genericPubSub = new GenericPubSub({});
